import config from "appConfig";

const theory = ` <h3 class=sectionHead>Introduction</h3>
<p>
   A general loudspeaker model is composed of three parts, the electrical one with electrical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>, the mechanical part with mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   and the acoustical part, the latter being often included in the mechanical part (through an equivalent membrane surface). The electrical and mechanical parts are connected by the force factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   (<a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-1.svg data-img-alt="Figure 1. General model of an electrodynamic loudspeaker.">Figure 1</a>).
   <figure class=figure>
<p><img src=${config.ASSETS_URL}/04/25/theory/figure-1.svg alt="Figure 1. General model of an electrodynamic loudspeaker."><figcaption class=caption>Figure 1. General model of an electrodynamic loudspeaker.</figcaption></figure>
<p>
   In the traditional Thiele-Small model, the electrical part is represented by an RL circuit in which
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   represent the voice-coil losses and inductance respectively. An ideal mass–spring–damper system with mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>, stiffness
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>, and mechanical resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   describes the mechanical part. As the Thiele-Small model does not take into account effects such as eddy currents on the electrical part or the viscoelastic effects such as creep on the mechanical part, there are many complex models for electrodynamic loudspeakers that go far beyond the scope of these lectures.
<p>
   This lecture describes a measurement method that estimates the force factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>, the electrical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and the mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   independently on each other. By studying
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   one can clearly see the influence of the effects such as eddy currents or creep.
<h3 class=sectionHead>Measurement method</h3>
<h4 class=subsectionHead>Prerequisites</h4>
<p>
   In the following, we consider that the loudspeaker under test has been mounted in an ”almost-infinite” baffle and measured using any wide-band signal and that the voltage, current, and velocity signals have been acquired. In the case of the example presented in following figures, we used a multi-tone signal consisting of 648 frequencies
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>f</mi>
   </math>
   logarithmically spaced between 10 Hz and 20 kHz, forming an angular frequency vector
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ω</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi mathvariant=italic>πf</mi>
   </math>
   with 648 points. Next we suppose that the frequency-domain quantities, i.e. voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>U</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>, current
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>I</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>, and velocity
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are calculated from the measured signals. For frequencies above 4 kHz, the vibrometer values
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>V</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are set to zero to ignore high-frequency modal behavior.
<h4 class=subsectionHead>
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   estimation
</h4>
<p>
   The input impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>in</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   of the loudspeaker estimated as the ratio between the measured voltage
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>U</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and current
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>I</mi>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>, depicted in <a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-2.svg data-img-alt="Figure 2. Input impedance of the loudspeaker estimated from the measured voltage and current.">Figure 2</a> for the measured data, is given as
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>+</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mfrac>
               <mrow>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>I</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   is the force factor of the loudspeaker and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is the electrical impedance of the voice coil, which can then be expressed as
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi>e</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>in</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-bin>−</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mfrac>
               <mrow>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>I</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(2)
</table>
<figure class=figure>
   <div class=center>
      <p><img src=${config.ASSETS_URL}/04/25/theory/figure-2.svg alt="Figure 2. Input impedance of the loudspeaker estimated from the measured voltage and current.">
   </div>
   <figcaption class=caption>Figure 2. Input impedance of the loudspeaker estimated from the measured voltage and current.</figcaption>
</figure>
<p>
   To estimate the force factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   independently of other model parameters we consider that the voice-coil impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   does not exhibit any resonance at low frequencies. Indeed, the resonance behavior of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>in</mi>
         </mrow>
      </msub>
   </math>
   is due to the mechanical part, in other words, due to the term
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
      <mfrac>
         <mrow>
            <mi>V</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mi>I</mi>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
      </mfrac>
   </math>. Consequently, we can evaluate Eq. (2) for different estimated values of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <mi mathvariant=italic>Bℓ</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
      <mo class=MathClass-punc>.</mo>
   </math>
   The value
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <mi mathvariant=italic>Bℓ</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   that minimizes the resonant behavior of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is then selected as the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   value of the loudspeaker. This process is graphically represented in <a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-3.svg data-img-alt="Figure 3. Graphical representation of the force factor <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Bℓ</mi></math> estimation from the measured data. The value of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mover accent=false class=mml-overline><mrow><mi mathvariant=italic>Bℓ</mi></mrow><mo accent=true>¯</mo></mover></math> that removes the resonance from the electric impedance <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>e</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> is selected as the best estimate of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Bℓ</mi></math>.">Figure 3</a>. In this particular example
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
      <mo class=MathClass-rel>=</mo>
   </math>
    4.8 T.m.
   <figure class=figure><img src=${config.ASSETS_URL}/04/25/theory/figure-3.svg alt="Figure 3. Graphical representation of the force factor <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Bℓ</mi></math> estimation from the measured data. The value of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mover accent=false class=mml-overline><mrow><mi mathvariant=italic>Bℓ</mi></mrow><mo accent=true>¯</mo></mover></math> that removes the resonance from the electric impedance <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>e</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> is selected as the best estimate of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Bℓ</mi></math>."><figcaption class=caption>Figure 3. Graphical representation of the force factor <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Bℓ</mi></math> estimation from the measured data. The value of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mover accent=false class=mml-overline><mrow><mi mathvariant=italic>Bℓ</mi></mrow><mo accent=true>¯</mo></mover></math> that removes the resonance from the electric impedance <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>Z</mi></mrow><mrow><mi>e</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> is selected as the best estimate of <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><mi mathvariant=italic>Bℓ</mi></math>.</figcaption></figure>
<h4 class=subsectionHead>Electrical impedance</h4>
<p>
   Once the value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>
   is estimated, we can separate the electrical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   using Eq. (2). The T&S model predicts the electrical impedance behavior being influenced by two constant parameters, resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>
   and inductance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
   </math>. The resistance is the real part of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>, and the inductance is the imaginary part of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   divided by the angular frequency
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi>ω</mi>
   </math>. These two quantities are deduced from real measured data and depicted in <a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-4.svg data-img-alt="Figure 4. Apparent resistance and inductance of the loudspeaker voice-coil, both depending on frequency, estimated from the measured data.">Figure 4</a>. It is clear from both figures that both quantities are highly dependent on frequency contrary to the T&S model in which both quantities are constant. For this reason they are called ”apparent resistance”
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and ”apparent inductance”
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>L</mi>
         </mrow>
         <mrow>
            <mi>e</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>.
   <figure class=figure><img src=${config.ASSETS_URL}/04/25/theory/figure-4.svg alt="Figure 4. Apparent resistance and inductance of the loudspeaker voice-coil, both depending on frequency, estimated from the measured data."><figcaption class=caption>Figure 4. Apparent resistance and inductance of the loudspeaker voice-coil, both depending on frequency, estimated from the measured data.</figcaption></figure>
<p>The reason for such a variation with frequency is the presence of eddy currents in the iron pole pieces. They increase the apparent resistance due to the thermal effects of eddy currents passing inside the iron and decrease the apparent inductance by mutual inductance. The eddy currents depend on frequency due to the skin effect [3].
<h4 class=subsectionHead>Mechanical impedance</h4>
<p>
   In the next step, we can estimate the mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   that includes also the acoustical radiation impedance. The mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   or mechanical admittance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Y</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
    can be deduced as
<table class=equation>
   <tr>
      <td>
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>Bℓ</mi>
            <mfrac>
               <mrow>
                  <mi>I</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mi>V</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   In <a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-5.svg data-img-alt="Figure 5. Absolute value of the mechanical admittance (a) and the mechanical impedance (b) as a function of frequency, estimated from the measured data.">Figure 5</a> both the mechanical admittance and mechanical impedance are depicted as a function of frequency. By observing both impedance curves, we can note two distinct parts. At low frequencies (up to 300 Hz) the behavior is driven by a damped mass-spring system with a resonance frequency near 55 Hz. At high frequencies (above 300 Hz) the behavior starts to become less predictable (see <a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-5.svg data-img-alt="Figure 5. Absolute value of the mechanical admittance (a) and the mechanical impedance (b) as a function of frequency, estimated from the measured data.">Figure 5b</a>) due to a non-piston motion of the mechanical part (break-up, modal behavior). Since the modal behavior is out of the scope of this paper and since we consider only the piston motion, we use only the data from low frequencies (between 20 Hz and 250 Hz) for further analysis of mechanical part.
   <figure class=figure><img src=${config.ASSETS_URL}/04/25/theory/figure-5.svg alt="Figure 5. Absolute value of the mechanical admittance (a) and the mechanical impedance (b) as a function of frequency, estimated from the measured data."><figcaption class=caption>Figure 5. Absolute value of the mechanical admittance (a) and the mechanical impedance (b) as a function of frequency, estimated from the measured data.</figcaption></figure>
<h5 class=likesubsubsectionHead>Mechanical Resistance</h5>
<p>
   The T&S model considers the mechanical impedance to behave like a mass-spring-damper system with mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>, stiffness
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>, and mechanical resistance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   related to the mechanical impedance as
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>Z</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-open>(</mo>
            <mi>ω</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <mi mathvariant=italic>jω</mi>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>R</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>+</mo>
            <mfrac>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>K</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>jω</mi>
               </mrow>
            </mfrac>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   The real part of the mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   represents mechanical losses. According to Eq. (4), the mechanical resistance is constant and equal to
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>R</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>. The real part of the mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   depicted in <a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-6.svg data-img-alt="Figure 6. Real part of the mechanical impedance representing the mechanical losses, estimated from the measured data.">Figure 6</a> for the measured data shows an important frequency dependency with frequency and is called ”apparent mechanical resistance”. Indeed, this kind of behavior is related to a creep effect [1, 2].
   <figure class=figure>
<div class=center><p><img src=${config.ASSETS_URL}/04/25/theory/figure-6.svg alt="Figure 6. Real part of the mechanical impedance representing the mechanical losses, estimated from the measured data."><figcaption class=caption>Figure 6. Real part of the mechanical impedance representing the mechanical losses, estimated from the measured data.</figcaption></div></figure>
<h5 class=likesubsubsectionHead>Moving Mass and Stiffness</h5>
<p>
   Finally, the imaginary part of the mechanical impedance
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>Z</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   represents the mechanical energy storage. According to the Thiele-Small model, the stiffness
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   is related to the moving mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   as
<table class=equation>
   <tr>
      <td style="font-size: 14px">
         <math display=block xmlns=http://www.w3.org/1998/Math/MathML class=equation>
            <msub>
               <mrow>
                  <mi>K</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <msup>
               <mrow>
                  <mi>ω</mi>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
            <mo class=MathClass-bin>−</mo>
            <mstyle class=text>
               <mtext>imag</mtext>
            </mstyle>
            <mrow>
               <mo form=prefix fence=true>{</mo>
               <mrow>
                  <msub>
                     <mrow>
                        <mi>Z</mi>
                     </mrow>
                     <mrow>
                        <mi mathvariant=italic>ms</mi>
                     </mrow>
                  </msub>
                  <mo class=MathClass-open>(</mo>
                  <mi>ω</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mo form=postfix fence=true>}</mo>
            </mrow>
            <mi>ω</mi>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   To find both moving mass and stiffness, one can fit the imaginary part of the mechanical impedance to the measured data. However, it is known that the stiffness is frequency dependent due to the creep effect and that the moving mass can be frequency dependent as well due to the inertial air load [4]. Nevertheless, at very low frequencies the moving mass should not vary with frequency a lot [4]. We consider it to be constant in the selected frequency region from 20 Hz to 250 Hz and we apply a similar technique as we used for the force factor estimation by plotting the apparent stiffness
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   calculated using Eq. (5) for several estimated mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>. In theory, the curve representing a constant apparent stiffness (independent of frequency) for a given value of mass then should give the estimated value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>
   and
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>M</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
   </math>.
<p>
<a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-7.svg data-img-alt="Figure 7. Estimation of the apparent stiffness <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>K</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> and the mass <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub></math> from the measured data.">Figure 7</a> shows the apparent stiffness
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   for different values of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>. The rough estimation (<a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-7.svg data-img-alt="Figure 7. Estimation of the apparent stiffness <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>K</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> and the mass <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub></math> from the measured data.">Figure 7a</a>) with
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   varying from 15 to 19 grams show that there is not a single flat
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   curve. For low estimates of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   (e.g., 15 g) the apparent stiffness drops quickly to negative values. On the other hand for high estimates of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   (e.g., 19 g) the apparent stiffness raises with frequency to very high values. A detailed variation of the mass
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   (<a href=# data-img-src=${config.ASSETS_URL}/04/25/theory/figure-7.svg data-img-alt="Figure 7. Estimation of the apparent stiffness <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>K</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> and the mass <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub></math> from the measured data.">Figure 7b</a>) shows that it is indeed very difficult to select a single value of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mover accent=false class=mml-overline>
         <mrow>
            <msub>
               <mrow>
                  <mi>M</mi>
               </mrow>
               <mrow>
                  <mi mathvariant=italic>ms</mi>
               </mrow>
            </msub>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   and one associated curve of
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>. It is clear that the
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <msub>
         <mrow>
            <mi>K</mi>
         </mrow>
         <mrow>
            <mi mathvariant=italic>ms</mi>
         </mrow>
      </msub>
      <mo class=MathClass-open>(</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is rising with frequency, which is a well-known phenomena due to the creep effect [1, 2].
   <figure class=figure><img src=${config.ASSETS_URL}/04/25/theory/figure-7.svg alt="Figure 7. Estimation of the apparent stiffness <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>K</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> and the mass <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub></math> from the measured data."><figcaption class=caption>Figure 7. Estimation of the apparent stiffness <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>K</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub><mo class=MathClass-open>(</mo><mi>ω</mi><mo class=MathClass-close>)</mo></math> and the mass <math display=inline xmlns=http://www.w3.org/1998/Math/MathML><msub><mrow><mi>M</mi></mrow><mrow><mi mathvariant=italic>ms</mi></mrow></msub></math> from the measured data.</figcaption></figure>
<h3 class=sectionHead>Conclusion</h3>
<p>
   This lecture shows a measurement method for electrodynamic loudspeakers. The estimation consists of several consecutive steps. First, we estimate the force factor
   <math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
      <mi mathvariant=italic>Bℓ</mi>
   </math>. Then, we separate the electrical and the mechanical impedance, and we show that the classical T&S parameters that are considered constant vary with frequency. These steps may help to understand why there is a large number of loudspeaker models available in the literature and their relation to physics.
<h3 class=likesectionHead>Glossary</h3>
<p>
<dl>
  <dt>RL
  <dd>Resistor, Inductor
  <dt>T&S
  <dd>Thiele & Small
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
   <dt class=thebibliography id=X0-knudsen1993low>[1]
   <dd class=thebibliography id=bib-1>
      <p>Morten H Knudsen and J Grue Jensen. “Low-frequency loudspeaker models that include suspension creep”. In: <span class=cmti-10>Journal of the Audio Engineering </span><span class=cmti-10>Society </span>41.1/2 (1993), pp. 3–18.
   <dt class=thebibliography id=X0-novak2016modeling>[2]
   <dd class=thebibliography id=bib-2>
      <p>Antonin Novak. “Modeling viscoelastic properties of loudspeaker suspensions using fractional derivatives”. In: <span class=cmti-10>Journal of the Audio </span><span class=cmti-10>Engineering Society </span>64.1/2 (2016), pp. 35–44.
   <dt class=thebibliography id=X0-vanderkooy1989a>[3]
   <dd class=thebibliography id=bib-3>
      <p>John Vanderkooy. “A Model of Loudspeaker Driver Impedance Incorporating Eddy Currents in the Pole Structure”. In: <span class=cmti-10>J. Audio Eng. Soc</span> 37.3 (1989), pp. 119–128.
   <dt class=thebibliography id=X0-vanderkooy2008inertial>[4]
   <dd class=thebibliography id=bib-4>
      <p>John Vanderkooy. “The inertial air load of a loudspeaker diaphragm”. In: <span class=cmti-10>Audio Engineering Society Convention 124</span>. Audio Engineering Society. 2008.
</dl>`;

export default theory;
